import React, { useEffect } from "react";
import { Button, Form, Input, Row, Col, message } from "antd";
import { API_METHODS } from "../../constants";
import { isEmpty } from "../../generalFunctions";
import useApi from "../../hooks/useApi";
import ServerDownResult from "../../components/common/ServerDownResult";
import { User, Phone, Mail, MapPin, Lock } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../../assets/logo.png";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

function Signup() {
  const [response, request] = useApi(
    "/user/auth/register",
    null,
    API_METHODS.POST
  );
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const SignupClicked = () => {
    form.validateFields().then((values) => {
      request(values);
    });
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  // Update the success handling in your Signup component
  // Replace the current success useEffect with this enhanced version:

  useEffect(() => {
    if (!isEmpty(response) && !response.isLoading) {
      if (response.data && response.data.message) {
        // SUCCESS: Show enhanced success message
        messageApi.open({
          type: "success",
          content: (
            <div>
              <div className="font-semibold mb-1">
                🎉 Registration Successful!
              </div>
              <div className="text-sm">
                Please check your email to verify your account before logging in
              </div>
            </div>
          ),
          duration: 6,
        });

        // Reset form
        form.resetFields();
      } else if (response.error) {
        // ERROR: Show error message
        messageApi.open({
          type: "error",
          content:
            response.error || "❌ Registration failed. Please try again.",
          duration: 4,
        });
      }
    }
  }, [response, messageApi, form]);
  return (
    <>
      {contextHolder}
      <HeaderComponent />

      {!isEmpty(response) ? (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 py-12 px-4">
          <div className="container mx-auto max-w-4xl">
            {/* Header with smaller logo */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img
                    src={logo}
                    alt="MahalaxmiPuja Logo"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-800">
                    MahalaxmiPuja
                  </h1>
                  <p className="text-orange-600 font-medium text-sm">
                    Shree Mahalaxmi Ambabai, Kolhapur
                  </p>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Create Your Account
              </h2>
              <p className="text-gray-600">
                Join our devotee community for authentic temple services
              </p>
            </div>

            {/* Signup Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-8">
              <Form layout="vertical" onFinish={SignupClicked} form={form}>
                {/* Personal Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-orange-500" />
                    Personal Information
                  </h3>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="First Name"
                        name="first_name"
                        rules={[
                          {
                            required: true,
                            message: "Please input your first name",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter your first name"
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        label="Last Name"
                        name="last_name"
                        rules={[
                          {
                            required: true,
                            message: "Please input your last name",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter your last name"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Contact Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Phone className="h-5 w-5 mr-2 text-orange-500" />
                    Contact Information
                  </h3>

                  <Form.Item
                    label="Email Address"
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: "Please input your email",
                      },
                      {
                        type: "email",
                        message: "Please enter a valid email",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<Mail className="h-4 w-4 text-gray-400" />}
                      placeholder="your.email@example.com"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Phone Number"
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message: "Please input your phone number",
                      },
                      {
                        pattern: /^[0-9]{10}$/,
                        message: "Please enter a valid 10-digit phone number",
                      },
                    ]}
                  >
                    <Input
                      size="large"
                      prefix={<Phone className="h-4 w-4 text-gray-400" />}
                      placeholder="9876543210"
                    />
                  </Form.Item>
                </div>

                {/* Security */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Lock className="h-5 w-5 mr-2 text-orange-500" />
                    Security
                  </h3>

                  <Form.Item
                    label="Password"
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please input your password!",
                      },
                      {
                        pattern:
                          /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,15}$/,
                        message:
                          "Password must be 8-15 characters with uppercase, digit, and special character (@$!%*?&)",
                      },
                    ]}
                  >
                    <Input.Password
                      size="large"
                      placeholder="Create a strong password"
                    />
                  </Form.Item>
                  <div className="text-xs text-gray-500 mt-1">
                    Password must contain: 8-15 characters, uppercase letter,
                    number, special character (@$!%*?&)
                  </div>
                </div>

                {/* Traditional Details */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    🕉️ Traditional Details (Optional)
                  </h3>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Gotra" name="gotra">
                        <Input size="large" placeholder="Gotra" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item label="Gan" name="gan">
                        <Input size="large" placeholder="Gan" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Address Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <MapPin className="h-5 w-5 mr-2 text-orange-500" />
                    Address Information (Optional)
                  </h3>

                  <Form.Item label="Address" name="address">
                    <Input.TextArea
                      rows={2}
                      size="large"
                      placeholder="House no., Street, Area"
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <Form.Item label="City" name="city">
                        <Input size="large" placeholder="City" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item label="State" name="state">
                        <Input size="large" placeholder="State" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Form.Item
                        label="Pincode"
                        name="pincode"
                        rules={[
                          {
                            pattern: /^[0-9]{6}$/,
                            message: "Please enter a valid 6-digit pincode",
                          },
                        ]}
                      >
                        <Input size="large" placeholder="400001" />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>

                {/* Terms and Actions */}
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                  <div className="text-sm text-gray-700">
                    By creating an account, you agree to our{" "}
                    <Link
                      to="/terms-conditions"
                      className="text-orange-600 hover:underline font-medium"
                    >
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link
                      to="/privacy-policy"
                      className="text-orange-600 hover:underline font-medium"
                    >
                      Privacy Policy
                    </Link>
                    . Your personal information will be used to provide you with
                    authentic temple services and spiritual guidance.
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    size="large"
                    onClick={handleLoginClick}
                    className="flex-1 h-12 border-orange-500 text-orange-600 hover:bg-orange-50 font-medium"
                  >
                    Already have an account? Login
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={response?.isLoading}
                    className="flex-1 h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 font-medium"
                  >
                    {response?.isLoading
                      ? "Creating Account..."
                      : "🚀 Create Account"}
                  </Button>
                </div>
              </Form>
            </div>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="text-gray-500 text-sm">
                🪔 May Shree Mahalaxmi Ambabai bless you with prosperity and
                happiness
              </p>
            </div>
          </div>
        </div>
      ) : (
        <ServerDownResult />
      )}

      <Footer />
    </>
  );
}

export default Signup;
